/**
 * Plain-language outcome of a notification job (one send, one scheduled
 * send, or one run of a recurring schedule). Pure functions — unit-tested in
 * pushOutcome.test.js.
 *
 * Counts alone ("0 of 41 delivered · 33 failed") hid WHY nothing arrived; the
 * backend now records per-reason breakdowns and any store-level problem with
 * native app push, and these helpers turn them into sentences.
 */

/** Why a whole job was not sent. */
export const SKIP_REASONS = {
  feature_disabled: "The feature was turned off for this store before it could send.",
  tenant_inactive: "The store was inactive at send time.",
  too_late: "The server was unavailable at the scheduled time, so it was not sent late (more than an hour after).",
  no_recipients: "Nobody was in the audience at send time.",
  series_cancelled: "Its recurring schedule was cancelled before it could send.",
};

/** Why individual recipients were skipped. */
export const SKIPPED_RECIPIENT = {
  no_devices: "no app or browser notifications enabled",
  opted_out: "turned these notifications off",
};

/** Why individual recipients failed. */
export const FAILED_RECIPIENT = {
  unavailable: "app notifications are not set up for this store",
  gone: "their device is no longer registered",
  rejected: "rejected by the push service",
  timeout: "the push service did not answer",
  transient: "the push service was unavailable",
  error: "an unexpected error",
};

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "33 — app notifications are not set up…, 2 — …" from a {reason: count} map. */
export function breakdownText(breakdown, labels) {
  const entries = Object.entries(breakdown || {}).filter(([, n]) => Number(n) > 0)
    .sort((a, b) => b[1] - a[1]);
  return entries.map(([k, n]) => `${n} — ${labels[k] || k.replace(/_/g, " ")}`).join("; ");
}

/** One-line outcome for a job ("3 of 5 delivered · 2 failed (2 — …)"). */
export function jobOutcome(job) {
  if (!job || job.status === "scheduled") return null;
  if (job.status === "cancelled") return "Cancelled before sending.";
  if (job.status === "skipped" || (job.status === "partial" && job.skip_reason)) {
    const why = SKIP_REASONS[job.skip_reason] || "Not sent.";
    return job.status === "partial" ? `Stopped part-way: ${why}` : why;
  }
  const total = job.recipients_total ?? job.attempted ?? 0;
  const parts = [`${job.delivered_users ?? 0} of ${total} delivered`];
  if (job.failed_users) {
    const why = breakdownText(job.failed_breakdown, FAILED_RECIPIENT);
    parts.push(`${job.failed_users} failed${why ? ` (${why})` : ""}`);
  }
  if (job.skipped_users) {
    const why = breakdownText(job.skipped_breakdown, SKIPPED_RECIPIENT);
    parts.push(`${job.skipped_users} skipped${why ? ` (${why})` : ""}`);
  }
  return parts.join(" · ");
}

/** Store-level problem that stopped app notifications, phrased for an admin. */
export function jobIssue(job) {
  if (!job?.app_channel_issue) return null;
  return `App notifications could not be sent: ${job.app_channel_issue}`;
}

/** "3 runs · 120 customers reached" for a recurring schedule. */
export function seriesTotals(series) {
  const runs = series?.runs ?? series?.occurrences_created ?? 0;
  const reached = series?.delivered_total ?? 0;
  return `${plural(runs, "run")} · ${plural(reached, "customer")} reached`;
}
